<script setup lang="ts">
import { computed, ref, type FunctionalComponent } from 'vue'
import { refDebounced } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useQuery as _useQuery, keepPreviousData } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth.ts'
import { queryFn } from '@/composables/api.ts'
import { useAccess } from '@/composables/useAccess.ts'
import { useCreditorSettings } from '@/composables/useCreditorSettings.ts'
import { useUserSettings } from '@/composables/useUserSettings.ts'
import { formatNumber, formatPercentage } from '@/utils/format'
import StatisticsSettingsDialog from './StatisticsSettingsDialog.vue'
import mdiTrendingUp from '~icons/mdi/trending-up'
import mdiTrendingDown from '~icons/mdi/trending-down'
import mdiTuneVerticalVariant from '~icons/mdi/tune-vertical-variant'
import mdiTrendingNeutral from '~icons/mdi/trending-neutral'

const STATISTIC_TYPES = ['reminder', 'debt_collection', 'enforcement', 'debt_surveillance', 'amortization'] as const
type StatisticType = (typeof STATISTIC_TYPES)[number]

function normalizeHidden(hidden: unknown): StatisticType[] {
  if (Array.isArray(hidden)) return hidden as StatisticType[]
  if (typeof hidden === 'string') return [hidden as StatisticType]
  return []
}

interface TrendStyle {
  bg: string
  text: string
  icon: string
  color: string
}

// All values resolve against the theme so chip colors track Vuetify's
// statusColors / semantic palette. Backgrounds use the slash-alpha syntax on
// the semantic role (success/error/secondary) so they share a tinted base;
// text/icon point at the same statusColor (green/red/grey) used by the `:color`
// prop on the tooltip chip. Earlier `rgb(green / 0.12)` was invalid CSS
// because `green` is a single token, not three space-separated channels;
// `var(--v-theme-success)` expands to the three channels and is valid.
const TREND_STYLES = {
  positive: {
    bg: 'rgb(var(--v-theme-success) / 0.12)',
    text: 'rgb(var(--v-theme-green))',
    icon: 'rgb(var(--v-theme-green))',
    color: 'green',
  },
  negative: {
    bg: 'rgb(var(--v-theme-error) / 0.12)',
    text: 'rgb(var(--v-theme-red))',
    icon: 'rgb(var(--v-theme-red))',
    color: 'red',
  },
  neutral: {
    bg: 'rgb(var(--v-theme-secondary) / 0.12)',
    text: 'rgb(var(--v-theme-grey))',
    icon: 'rgb(var(--v-theme-grey))',
    color: 'grey',
  },
} as const satisfies Record<string, TrendStyle & { color: string }>

const { selectedCreditorIds, hasSelection, serverCreditorSettings } = useCreditorSettings()

const { settings: userSettings } = useUserSettings()

const { t } = useI18n()
const { isSolicitor } = useAuth()
const access = useAccess()
// Mirrors the route's own access gate (`cases`). Hiding the section + skipping
// the query when the user can't see cases avoids a guaranteed 403.
const hasCasesAccess = access.canAccessResource('cases')

const showStatisticsDialog = ref(false)

// `enabledStatistics` decides which cards to render. Driven off the user's
// selection + each creditor's `dashboard_statistics.hidden` setting. Creditors
// without settings are treated as having defaults (no hidden stats).
const enabledStatistics = computed<StatisticType[]>(() => {
  const relevantCreditorIds = selectedCreditorIds.value

  if (relevantCreditorIds.length === 0) {
    return [...STATISTIC_TYPES]
  }

  const hiddenCounts = new Map<StatisticType, number>(STATISTIC_TYPES.map((stat) => [stat, 0]))

  // Use the server snapshot, not `settings()`, so this computed doesn't
  // re-evaluate on every pendingSettings mutation during bulk dialog saves.
  for (const creditorId of relevantCreditorIds) {
    const hiddenArray = normalizeHidden(serverCreditorSettings(creditorId)?.settings.dashboard_statistics?.hidden)
    for (const hiddenStat of hiddenArray) {
      if (hiddenCounts.has(hiddenStat)) {
        hiddenCounts.set(hiddenStat, (hiddenCounts.get(hiddenStat) ?? 0) + 1)
      }
    }
  }

  return STATISTIC_TYPES.filter((stat) => (hiddenCounts.get(stat) ?? 0) < relevantCreditorIds.length)
})

// Stricter than the codegen-generated `Dashboard-StatisticsSummaryResponse`:
// `trends.<stat>` is `Record<string, never>` there (the SwaggerService
// cerberus_convert is shallow and drops the nested `{last30, prev30}` shape).
type StatsRecord = Record<StatisticType, number>
interface DashboardStatisticsSummary {
  open_case_counts: StatsRecord
  trends: Record<StatisticType, { last30: number; prev30: number }>
  level: 'creditor' | 'account'
}

const summaryBody = computed(() => {
  const selection = userSettings.value.dashboard?.creditor_selection
  const tagSelection = userSettings.value.dashboard?.creditor_tag_selection ?? []
  return {
    all_creditors_selected: selection?.all_selected === true,
    included_creditors: (selection?.included ?? []).map((item: { creditor: string }) => item.creditor),
    excluded_creditors: (selection?.excluded ?? []).map((item: { creditor: string }) => item.creditor),
    creditor_tags: tagSelection.map((item: { creditor_tag: string }) => item.creditor_tag),
  }
})

const canFetchSummary = computed(() => hasSelection.value && hasCasesAccess.value)

// Coalesce rapid filter toggles into one request.
const debouncedSummaryBody = refDebounced(summaryBody, 300)

// Raw `_useQuery` because the codebase wrapper is GET-only. POST is used here
// as a read-only aggregation (request body would exceed URL length limits);
// `useMutation` is wrong semantically and lacks the `staleTime` +
// `keepPreviousData` behaviour we depend on (cards must not flash to zero
// during refetches). queryKey-based invalidation from
// `useCreditorSettings.invalidateSettingsQueries` matches `/dashboard--statistics-summary`
// via prefix and works unchanged.
const summaryQuery = _useQuery({
  // @ts-expect-error wrapper's queryKey augmentation is ['get', ...]; this is POST.
  queryKey: computed(() => ['post', '/dashboard--statistics-summary', { body: debouncedSummaryBody.value }] as const),
  queryFn,
  // Only fetch when the user has a selection AND `cases` access. Without
  // a selection, `open_case_counts` is hardcoded to zero server-side and
  // the trends would fall back to `statistics--account-days` — meaningless
  // beside the zero counts. Without `cases` access the route 403:s. The
  // whole section hides on the same gate, so the query stays in sync.
  enabled: canFetchSummary,
  staleTime: 30_000,
  placeholderData: keepPreviousData,
})

const summary = computed(() => summaryQuery.data.value as DashboardStatisticsSummary | undefined)
const isLoadingSummary = computed(() => summaryQuery.isFetching.value)

const stats = computed(() =>
  enabledStatistics.value.map((state) => {
    const count = summary.value?.open_case_counts[state] ?? 0
    const trend = summary.value?.trends[state] ?? { last30: 0, prev30: 0 }
    const percent = trend.prev30 === 0 ? 0 : Math.round((trend.last30 / trend.prev30 - 1) * 100)
    return {
      key: state,
      title: `common.${state}`,
      count,
      trendPercent: percent,
      trendLast30: trend.last30,
      trendPrev30: trend.prev30,
      trendIcon: getTrendIcon(percent),
      trendStyle: getTrendStyle(percent),
    }
  })
)

function getTrendIcon(percent: number): FunctionalComponent {
  if (percent > 0) return mdiTrendingUp
  if (percent < 0) return mdiTrendingDown
  return mdiTrendingNeutral
}

function getTrendStyle(percent: number): TrendStyle {
  if (percent > 0) return TREND_STYLES.positive
  if (percent < 0) return TREND_STYLES.negative
  return TREND_STYLES.neutral
}
</script>

<template>
  <section v-if="canFetchSummary">
    <header class="mb-4 d-flex justify-space-between align-center">
      <h2 class="text-headline-medium">{{ t('common.statistics') }}</h2>
      <v-btn
        v-if="!isSolicitor"
        variant="text"
        @click="showStatisticsDialog = true"
      >
        <v-icon
          start
          :icon="mdiTuneVerticalVariant"
        />
        {{ t('common.adjust_statistics') }}
      </v-btn>
    </header>

    <div
      class="stats-grid"
      :style="`--stats-count: ${stats.length}`"
    >
      <v-card
        v-for="stat in stats"
        :key="stat.key"
        class="pa-6 stat-card"
        :class="{ 'stat-card--loading': isLoadingSummary }"
        min-height="126"
        variant="outlined"
        :loading="isLoadingSummary"
      >
        <h3 class="text-title-small font-weight-medium text-medium-emphasis text-truncate mb-4">
          {{ t(stat.title) }}
        </h3>

        <div class="d-flex align-center flex-wrap ga-3">
          <div class="d-flex align-baseline">
            <span class="text-display-medium font-family-body mr-2">{{ formatNumber(stat.count) }}</span>
            <span class="font-weight-medium">{{ t('common.unit_pieces') }}</span>
          </div>

          <v-tooltip
            location="bottom"
            width="270"
          >
            <template #activator="{ props: tooltipProps }">
              <v-chip
                v-bind="tooltipProps"
                size="small"
                class="d-flex ga-2"
                :style="{
                  backgroundColor: stat.trendStyle.bg,
                  color: stat.trendStyle.text,
                }"
              >
                <template #prepend>
                  <v-icon
                    size="16"
                    :style="{ color: stat.trendStyle.icon }"
                  >
                    <component :is="stat.trendIcon" />
                  </v-icon>
                </template>
                {{ formatPercentage(stat.trendPercent, { alreadyPercent: true }) }}
              </v-chip>
            </template>

            <div class="py-1">
              <div class="d-flex justify-space-between align-center mb-2">
                <span>{{ t('common.last_thirty_days') }}</span>
                <v-chip
                  size="x-small"
                  variant="flat"
                  :color="stat.trendStyle.color"
                >
                  <template #prepend>
                    <v-icon size="12">
                      <component :is="stat.trendIcon" />
                    </v-icon>
                  </template>
                  {{ formatPercentage(stat.trendPercent, { alreadyPercent: true }) }}
                </v-chip>
              </div>
              <div class="d-flex align-baseline mb-2">
                <span class="text-headline-medium font-family-body font-weight-medium mr-2">
                  {{ formatNumber(stat.trendLast30) }}
                </span>
                <span class="text-title-small">{{ t('common.unit_pieces') }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span>{{ t('common.previous_thirty_days') }}</span>
                <span class="font-weight-medium">
                  {{ formatNumber(stat.trendPrev30) }} {{ t('common.unit_pieces') }}
                </span>
              </div>
            </div>
          </v-tooltip>
        </div>
      </v-card>
    </div>

    <statistics-settings-dialog
      v-model="showStatisticsDialog"
      :description="t('common.adjust_statistics_help')"
    />
  </section>
</template>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(var(--stats-count, 5), minmax(0, 1fr));
  gap: 24px;
}

@media (max-width: 960px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  transition: opacity 0.2s ease;
}

.stat-card--loading {
  opacity: 0.55;
}
</style>
